/**
 * The guide piece's standard look (the prototype's About: the guide beside its photo).
 * Draws the behavior half's state; holds no rule. Draws nothing without a guide.
 */
import { useId } from 'react';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import type { GuideRuntime } from './useGuide';
import css from './Guide.module.css';
import { contract } from './contract';

const part = partsOf(contract);

export function GuideView({ state, options }: GuideRuntime) {
  // Placed on a custom page (web-studio-sites D4): no band, no frame, no fixed anchor.
  const placed = usePlaced();
  const placedTitle = useId();
  if (!state.name) return null;
  const photo = options.showPhoto ? state.photoUrl : null;
  const titleId = placed ? placedTitle : 'guideTitle';
  return (
    <section
      className={placed ? undefined : state.followsStory ? css.afterStory : shared.sec}
      // With no story above it, this section is the home's About.
      id={placed || state.followsStory ? undefined : 'about'}
      aria-labelledby={state.heading ? titleId : undefined}
      aria-label={state.heading ? undefined : state.name}
      {...pieceRoot(contract)}
      {...part('section')}
    >
      <div className={placed ? undefined : shared.wrap}>
        <div className={`${css.guide} ${photo ? '' : css.noPhoto}`}>
          {photo && (
            <div className={css.photo} {...part('photo')}>
              <img src={photo} alt={state.name} loading="lazy" />
            </div>
          )}
          <div className={css.text}>
            {options.eyebrow && (
              <div className={shared.eyebrow} {...part('eyebrow')}>
                {options.eyebrow}
              </div>
            )}
            {state.heading && (
              <h2 className={`${shared.h2} ${css.heading}`} id={titleId} {...part('heading')}>
                {state.heading}
              </h2>
            )}
            <h3 className={`${shared.h3} ${css.name}`} {...part('name')}>
              {state.name}
            </h3>
            {state.role && (
              <p className={css.role} {...part('role')}>
                {state.role}
              </p>
            )}
            {state.credentials && (
              <div className={css.facts} {...part('facts')}>
                <div className={css.fact}>
                  <div className={css.factK}>Credentials</div>
                  <div className={css.factV} {...part('credentials')}>
                    {state.credentials}
                  </div>
                </div>
              </div>
            )}
            {state.bio.length > 0 && (
              <div className={`${shared.prose} ${css.bio}`} {...part('bio')}>
                {state.bio.map((p, i) => (
                  <p key={i} {...part('paragraph')}>
                    {p}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
